import ReactDOM from 'react-dom';
import { Button, ButtonVariant } from '../../../../shared/components/Buttons';
import { TextField } from '../../../../shared/components/TextField';
import {
  Card,
  CardBody,
  CardContent,
  CardStyle,
} from '../../../../shared/components/Card';
import PasswordUpdatedSuccessIcon from '../../../../assets/svg/PasswordUpdatedSuccess.svg';
import { Lock, Eye, EyeOff } from 'react-feather';
import { useUpdatePasswordForm } from '../../hooks/useUpdatePasswordForm';
import labels from '../../constants/pl.json';

const UpdatePasswordForm = () => {
  const {
    showCurrentPassword,
    showPassword,
    showConfirmPassword,
    currentPasswordField,
    passwordField,
    confirmPasswordField,
    displayCurrentPassword,
    displayPassword,
    displayConfirmPassword,
    passwordValidation,
    globalError,
    globalErrorMessage,
    isSubmitting,
    isPasswordUpdated,
    currentPasswordInputRef,
    passwordInputRef,
    confirmPasswordInputRef,
    handleToggleCurrentPassword,
    handleTogglePassword,
    handleToggleConfirmPassword,
    handleCurrentPasswordChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    handleCloseSuccess,
  } = useUpdatePasswordForm();

  return (
    <div className="relative w-full max-w-[700px]">
      <form
        onSubmit={handleSubmit}
        className={`flex flex-col gap-5 w-full ${isPasswordUpdated ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <div className="flex flex-col gap-6 ">
          <div className="flex flex-col gap-2">
            <h1 className="font-[400] text-[18px] text-neutral-10">
              Zmień hasło
            </h1>
            <p className="text-bodyMedium text-[14px] text-on-surface-variant">
              Wprowadź dotychczasowe hasło i ustaw nowe, aby przywrócić
              bezpieczeństwo swojego konta.
            </p>
          </div>

          <div className="flex flex-col gap-5 max-w-90">
            {/* Current Password Field */}
            <div className="flex flex-col">
              <TextField
                ref={currentPasswordInputRef}
                autoFocus={true}
                disabled={isSubmitting}
                label="Dotychczasowe hasło"
                showClearButton={false}
                error={currentPasswordField.hasError}
                errorMessage={currentPasswordField.errorMessage}
                fullWidth={true}
                labelColor="#474A4C"
                leadingIcon={<Lock size={20} color="#474A4C" />}
                value={displayCurrentPassword}
                onChange={
                  handleCurrentPasswordChange as (
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => void
                }
                trailingIcon={
                  <button
                    type="button"
                    onClick={handleToggleCurrentPassword}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label={
                      showCurrentPassword
                        ? labels.ARIA_HIDE_PASSWORD
                        : labels.ARIA_SHOW_PASSWORD
                    }
                    className="cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primary-color-40"
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={20} color="#474A4C" />
                    ) : (
                      <Eye size={20} color="#474A4C" />
                    )}
                  </button>
                }
                type="text"
              />
            </div>

            {/* New Password Field */}
            <div className="flex flex-col gap-2">
              <TextField
                ref={passwordInputRef}
                disabled={isSubmitting}
                label="Nowe hasło"
                showClearButton={false}
                error={passwordField.hasError}
                errorMessage={passwordField.errorMessage}
                fullWidth={true}
                labelColor="#474A4C"
                leadingIcon={<Lock size={20} color="#474A4C" />}
                value={displayPassword}
                onChange={
                  handlePasswordChange as (
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => void
                }
                trailingIcon={
                  <button
                    type="button"
                    onClick={handleTogglePassword}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label={
                      showPassword
                        ? labels.ARIA_HIDE_PASSWORD
                        : labels.ARIA_SHOW_PASSWORD
                    }
                    className="cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primary-color-40"
                  >
                    {showPassword ? (
                      <EyeOff size={20} color="#474A4C" />
                    ) : (
                      <Eye size={20} color="#474A4C" />
                    )}
                  </button>
                }
                type="text"
              />

              {/* Password Requirements */}
              {passwordField.value &&
                !(
                  passwordValidation.hasMinLength &&
                  passwordValidation.hasUppercase &&
                  passwordValidation.hasLowercase &&
                  passwordValidation.hasSpecialChar &&
                  passwordValidation.hasNumber &&
                  confirmPasswordField.value &&
                  confirmPasswordField.value === passwordField.value
                ) && (
                  <div className="flex flex-col gap-1">
                    <p className="text-[12px] font-normal text-neutral-40">
                      Hasło musi zawierać co najmniej:
                    </p>
                    <ul className="flex flex-col pl-4 list-disc">
                      <li
                        className={`text-[12px] font-normal ${
                          passwordValidation.hasMinLength
                            ? 'text-success-40'
                            : 'text-error-40'
                        }`}
                      >
                        8+ znaków
                      </li>
                      <li
                        className={`text-[12px] font-normal ${
                          passwordValidation.hasUppercase
                            ? 'text-success-40'
                            : 'text-error-40'
                        }`}
                      >
                        1 wielką literę
                      </li>
                      <li
                        className={`text-[12px] font-normal ${
                          passwordValidation.hasLowercase
                            ? 'text-success-40'
                            : 'text-error-40'
                        }`}
                      >
                        1 małą literę
                      </li>
                      <li
                        className={`text-[12px] font-normal ${
                          passwordValidation.hasSpecialChar
                            ? 'text-success-40'
                            : 'text-error-40'
                        }`}
                      >
                        1 znak specjalny
                      </li>
                      <li
                        className={`text-[12px] font-normal ${
                          passwordValidation.hasNumber
                            ? 'text-success-40'
                            : 'text-error-40'
                        }`}
                      >
                        1 cyfrę
                      </li>
                    </ul>
                  </div>
                )}
            </div>

            {/* Confirm Password Field */}
            <div className="flex flex-col gap-2">
              <TextField
                ref={confirmPasswordInputRef}
                label="Potwierdź hasło"
                showClearButton={false}
                disabled={isSubmitting}
                error={confirmPasswordField.hasError}
                errorMessage={confirmPasswordField.errorMessage}
                fullWidth={true}
                labelColor="#474A4C"
                leadingIcon={<Lock size={20} color="#474A4C" />}
                value={displayConfirmPassword}
                onChange={
                  handleConfirmPasswordChange as (
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => void
                }
                trailingIcon={
                  <button
                    type="button"
                    onClick={handleToggleConfirmPassword}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label={
                      showConfirmPassword
                        ? labels.ARIA_HIDE_CONFIRM_PASSWORD
                        : labels.ARIA_SHOW_CONFIRM_PASSWORD
                    }
                    className="cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primary-color-40"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} color="#474A4C" />
                    ) : (
                      <Eye size={20} color="#474A4C" />
                    )}
                  </button>
                }
                type="text"
              />
            </div>

            {globalError && globalErrorMessage && (
              <p className="text-[12px] font-normal leading-[16px] tracking-[0.4px] text-error-40">
                {globalErrorMessage}
              </p>
            )}
          </div>
        </div>

        <Button
          label={isSubmitting ? 'Aktualizowanie...' : 'Zmień hasło'}
          variant={ButtonVariant.Primary}
          size="Medium"
          className="cursor-pointer max-w-90"
          buttonType="Square"
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
        />
      </form>

      {/* Success Card Overlay */}
      {isPasswordUpdated &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <Card style={CardStyle.Elevated} width={480}>
              <CardBody padding="large">
                <div className="flex flex-col items-center gap-6 text-center">
                  <div className="flex items-center justify-center w-[137px] h-[137px]">
                    <PasswordUpdatedSuccessIcon />
                  </div>
                  <CardContent
                    title={labels.SUCCESS_TITLE}
                    description={labels.SUCCESS_DESCRIPTION}
                    className="px-0 [&>p:first-child]:!text-on-surface-variant [&>p:first-child]:!text-[18px] [&>p.mt-2]:!text-on-surface-variant [&>p.mt-2]:!mt-4"
                  />
                  <Button
                    label={labels.SUCCESS_BUTTON}
                    variant={ButtonVariant.Primary}
                    size="Medium"
                    className="!font-medium !text-[14px] cursor-pointer"
                    onClick={handleCloseSuccess}
                  />
                </div>
              </CardBody>
            </Card>
          </div>,
          document.body
        )}
    </div>
  );
};

export default UpdatePasswordForm;
